/** Sub-components for the GR1DD page (Neural Lattice). */
import { Link } from "react-router-dom";

export const GRIDD_CLASSIFICATIONS = ["UNCLASSIFIED", "CONFIDENTIAL", "SECRET", "OMEGA"];
export const GRIDD_STATUSES = ["ACTIVE", "REDACTED", "LOST"];

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function GateScreen({ onUnlock, error, loading, pass, setPass }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="gridd-gate">
      <div className="w-full max-w-md border border-[#333] p-8 sm:p-10 bg-[#0a0a0a] space-y-7">
        <div className="space-y-3">
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            Neural Network · GR1DD
          </div>
          <div className="flex items-center gap-3">
            <span className="tiv-stamp">OMEGA·LATTICE</span>
            <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[#555]">Σ recursive</span>
          </div>
          <h1 className="font-serif-display text-3xl tracking-tighter">Lattice Sealed</h1>
          <p className="text-xs text-[#555] leading-relaxed">
            The GR1DD holds nodes which were never meant to be read consecutively.
            Speak the seal-word to reach them. Master keys pass without speech.
          </p>
        </div>

        <form onSubmit={onUnlock} className="space-y-4">
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">
              GR1DD Passphrase
            </div>
            <input
              type="password"
              className="tiv-input"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              autoFocus
              required
              data-testid="gridd-passphrase-input"
            />
          </label>
          {error && (
            <div className="text-xs text-[#721C1C] border border-[#721C1C] p-2 tracking-wide" data-testid="gridd-error">
              {error}
            </div>
          )}
          <button className="tiv-btn w-full" type="submit" disabled={loading} data-testid="gridd-unlock-btn">
            {loading ? "verifying..." : "Open the Lattice"}
          </button>
        </form>

        <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] text-center pt-2 border-t border-[#333]">
          <Link to="/admin/console" className="hover:text-[#E3E1D9]">↩ Return to console</Link>
        </div>
      </div>
    </div>
  );
}

export function GriddHeader({ isMaster, creating, setCreating, newDesig, setNewDesig, newTitle, setNewTitle, onCreate }) {
  return (
    <header className="space-y-4">
      <div className="flex items-center gap-3 text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
        <span>Admin Panel · Δ Recursive</span>
        <span className="h-px flex-1 bg-[#333]" />
        {isMaster ? (
          <span className="text-[#721C1C]">◈ MASTER PASS</span>
        ) : (
          <span className="tiv-blink text-[#721C1C]">SEAL OPEN</span>
        )}
      </div>
      <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
        Neural Network · GR1DD
      </h1>
      <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
        Hidden nodes. Recursive, self-referential, self-modifying records. The lattice has been
        observed to re-arrange its own entries when not under direct observation. Inscribe with care.
      </p>
      <div className="flex flex-wrap gap-3 pt-2">
        {!creating && (
          <button className="tiv-btn" onClick={() => setCreating(true)} data-testid="gridd-new-btn">
            + Inscribe Node
          </button>
        )}
        <Link to="/admin/console" className="tiv-btn" data-testid="gridd-back-link">↩ Console</Link>
      </div>
      {creating && (
        <div className="border border-[#333] p-5 bg-[#0a0a0a] grid grid-cols-1 sm:grid-cols-12 gap-3 sm:items-end mt-4" data-testid="gridd-new-form">
          <label className="sm:col-span-3">
            <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">Designation</div>
            <input className="tiv-input" placeholder="GR1DD//δ" value={newDesig} onChange={(e) => setNewDesig(e.target.value)} data-testid="gridd-new-designation" />
          </label>
          <label className="sm:col-span-6">
            <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">Title</div>
            <input autoFocus className="tiv-input" placeholder="Title of this node..." value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onCreate()} data-testid="gridd-new-title" />
          </label>
          <div className="sm:col-span-3 flex gap-2">
            <button className="tiv-btn flex-1" onClick={onCreate} data-testid="gridd-new-save">Inscribe</button>
            <button className="tiv-btn" onClick={() => { setCreating(false); setNewTitle(""); setNewDesig(""); }} data-testid="gridd-new-cancel">Discard</button>
          </div>
        </div>
      )}
    </header>
  );
}

function RedactedNodeBlock() {
  return (
    <div className="border border-[#333] p-6 bg-[#0a0a0a] space-y-2">
      <div className="tiv-redact text-xl tracking-widest" style={{ padding: "0.5rem 1rem" }}>
        ████████████████████████████████████████████████████
      </div>
      <div className="tiv-redact text-xl tracking-widest" style={{ padding: "0.5rem 1rem" }}>
        ████████████████████████████████████
      </div>
      <div className="text-[0.65rem] tracking-[0.4em] uppercase text-[#721C1C] mt-2">
        [redacted // recursive]
      </div>
    </div>
  );
}

function NodeSidebar({ f, view, isEditing, draft, setDraft }) {
  return (
    <aside className="lg:col-span-3 space-y-3 text-xs font-mono-data">
      <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555]">{f.id}</div>
      <div className="font-serif-display text-3xl text-[#E3E1D9]">
        {view.designation || "GR1DD//?"}
      </div>
      {isEditing ? (
        <div className="space-y-2">
          <input className="tiv-input" value={draft.designation}
            onChange={(e) => setDraft({ ...draft, designation: e.target.value })}
            placeholder="GR1DD//δ" data-testid={`gridd-edit-designation-${f.id}`} />
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Classification</div>
            <select className="tiv-input" value={draft.classification}
              onChange={(e) => setDraft({ ...draft, classification: e.target.value })}
              data-testid={`gridd-edit-classification-${f.id}`}>
              {GRIDD_CLASSIFICATIONS.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Status</div>
            <select className="tiv-input" value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              data-testid={`gridd-edit-status-${f.id}`}>
              {GRIDD_STATUSES.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Date</div>
            <input className="tiv-input" value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              placeholder="MMXXIV.IX.30" />
          </label>
        </div>
      ) : (
        <div className="space-y-1 text-[#A3A3A3]">
          <div className="flex justify-between"><span>Class</span><span className="text-[#E3E1D9]">{f.classification}</span></div>
          <div className="flex justify-between"><span>Status</span><span className="text-[#E3E1D9]">{f.status}</span></div>
          <div className="flex justify-between"><span>Date</span><span className="text-[#E3E1D9]">{f.date || "—"}</span></div>
        </div>
      )}
    </aside>
  );
}

function NodeBody({ f, view, isEditing, draft, setDraft }) {
  const redacted = view.body === "[REDACTED]" || !view.body;
  return (
    <div>
      <div className="section-label">
        <span>Node · Record</span>
        <span>§ recursive</span>
      </div>
      {isEditing ? (
        <textarea className="tiv-textarea" value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          placeholder="the lattice fragment..."
          data-testid={`gridd-edit-body-${f.id}`} />
      ) : redacted ? (
        <RedactedNodeBlock />
      ) : (
        <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">{view.body}</div>
      )}
    </div>
  );
}

function NodePlates({ images, isEditing, onUpload, removeImage }) {
  return (
    <div>
      <div className="section-label">
        <span>Plates</span>
        <span>{(images || []).length}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(images || []).map((src, i) => (
          <div key={`${src.slice(-24)}-${i}`} className="border border-[#333] aspect-square overflow-hidden relative group">
            <img src={src} alt={`plate-${i}`} className="w-full h-full object-cover tiv-img" />
            {isEditing && (
              <button onClick={() => removeImage(i)}
                className="absolute top-1 right-1 tiv-btn tiv-btn-danger text-[0.55rem] px-2 py-1 opacity-0 group-hover:opacity-100">
                Burn
              </button>
            )}
          </div>
        ))}
        {isEditing && (
          <label className="border border-dashed border-[#333] aspect-square flex items-center justify-center cursor-crosshair hover:border-[#E3E1D9]">
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
            <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">+ Affix Plate</span>
          </label>
        )}
      </div>
    </div>
  );
}

export function GriddNode({ f, isEditing, draft, setDraft, onStartEdit, onSave, onCancel, onRemove, onUpload, removeImage }) {
  const view = isEditing ? draft : f;
  return (
    <article className="grid grid-cols-1 lg:grid-cols-12 gap-8 border-t border-[#333] pt-10" data-testid={`gridd-node-${f.id}`}>
      <NodeSidebar f={f} view={view} isEditing={isEditing} draft={draft} setDraft={setDraft} />
      <div className="lg:col-span-9 space-y-5">
        {isEditing ? (
          <input className="tiv-input text-2xl" value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="title of this node"
            data-testid={`gridd-edit-title-${f.id}`} />
        ) : (
          <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight tracking-tighter">
            {f.title || <span className="text-[#555] italic">[ untitled node ]</span>}
          </h2>
        )}
        <NodeBody f={f} view={view} isEditing={isEditing} draft={draft} setDraft={setDraft} />
        <NodePlates images={view.images} isEditing={isEditing} onUpload={onUpload} removeImage={removeImage} />
        <div className="flex gap-3 pt-4">
          {isEditing ? (
            <>
              <button className="tiv-btn" onClick={onSave} data-testid={`gridd-save-${f.id}`}>Seal & Save</button>
              <button className="tiv-btn" onClick={onCancel} data-testid={`gridd-cancel-${f.id}`}>Discard</button>
            </>
          ) : (
            <>
              <button className="tiv-btn" onClick={() => onStartEdit(f)} data-testid={`gridd-edit-${f.id}`}>Re-weave Node</button>
              <button className="tiv-btn tiv-btn-danger" onClick={() => onRemove(f.id)} data-testid={`gridd-delete-${f.id}`}>Sever</button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}
